import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, message } from "antd";
import { AwsS3ImageService } from "../../../../Services/AwsS3ImageService/AwsS3ImageService";
import { useDispatch, useSelector } from "react-redux";
import { PostInvoiceImageService } from "../../../../Services/InvoiceService/InvoiceService";
import { DeleteOutlined } from "@ant-design/icons";
import "./InvoiceUploadModal.css";
import CancelBtn from "../../../../Components/CustomButtons/CancelBtn/CancelBtn";
import AlertMessage from "../../../../Components/AlertMessage/AlertMessage";
import ProgressBar from "../../../../Components/ProgressBar/ProgressBar";
import { buttonFlag } from "../../../../Reducer/AddInvoiceReducer";
import { CheckUploadPermission } from "../../../../Services/AiService/CheckUploadPermission";
import { PDFDocument } from "pdf-lib";

type UploadMode = "per-file" | "per-page";

interface InvoiceUploadModalProps {
  visible: boolean;
  onClose: () => void;
  SelectedClientID: string;
  onSuccess: () => void;
  setDisableInvoiceButton: React.Dispatch<React.SetStateAction<boolean>>;
}

const InvoiceUploadModal: React.FC<InvoiceUploadModalProps> = ({
  visible,
  onClose,
  SelectedClientID,
  onSuccess,
  setDisableInvoiceButton,
}) => {
  const selectedClient = useSelector(
    (state: any) => state.selectedClient.selectedClient
  );
  const applicationType = useSelector(
    (state: any) => state.applicationType.applicationType
  );
  const selectedSupplierStatement = applicationType === "supplierStatements";
  const [showSupplierStatementView, setShowSupplierStatementView] = useState(
    selectedSupplierStatement
  );
  const isSupplierStatement = showSupplierStatementView;

  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>("per-file");
  const uploadCancelled = useRef(false);
  const maxFileCount =  100;
  const [closeButon, setcloseButon] = useState(false);

  const MAX_PAGES_PER_PAGE_MODE = 200;

  const getPdfPageCount = async (file: File): Promise<number | null> => {
    try {
      const buffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(buffer, { ignoreEncryption: true });
      return pdfDoc.getPageCount();
    } catch (error) {
      console.error(`Failed to read page count for "${file.name}":`, error);
      return null; // couldn't parse — let it through format/size checks, don't silently block it
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    setUploadProgress(0);
  }, [isSupplierStatement]);

  useEffect(() => {
    if (visible) {
      setShowSupplierStatementView(selectedSupplierStatement);
    }
    if (visible && selectedSupplierStatement) {
      setUploadMode("per-file");
      setUploadedImages([]);
    }
  }, [visible, selectedSupplierStatement]);

  const acceptedFormats =
    uploadMode === "per-page"
      ? ["application/pdf"]
      : ["image/jpg", "image/jpeg", "image/png", "application/pdf"];

  const acceptAttr =
    uploadMode === "per-page" ? ".pdf" : ".jpg,.jpeg,.pdf,.png";

  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_PDF_SIZE = 40 * 1024 * 1024; // 40MB

  const getSizeLimitForFile = (file: File) =>
    file.type === "application/pdf" ? MAX_PDF_SIZE : MAX_IMAGE_SIZE;

  const formatSizeLimit = (bytes: number) => `${bytes / (1024 * 1024)}MB`;

  const showOversizedFilesAlert = (oversizedFiles: File[]) => {
    if (oversizedFiles.length === 0) return;

    if (oversizedFiles.length === 1) {
      const file = oversizedFiles[0];
      AlertMessage({
        text: `"${file.name}" exceeds the ${formatSizeLimit(
          getSizeLimitForFile(file)
        )} size limit and was not added.`,
        type: "error",
        duration: 6,
      });
    } else {
      const details = oversizedFiles
        .map(
          (file) =>
            `${file.name} (limit: ${formatSizeLimit(getSizeLimitForFile(file))})`
        )
        .join(", ");
      AlertMessage({
        text: `The following files exceed their size limits and were not added: ${details}`,
        type: "error",
        duration: 6,
      });
    }
  };
  const fileLimitHint =
    isSupplierStatement
      ? "10 MB maximum for images. 40 MB maximum for PDFs."
      : uploadMode === "per-page"
      ? "File limits: 40MB for multi-page PDFs"
      : "File limits: 10MB for images/PDFs · 40MB for multi-page PDFs";

  const getSupplierStatementValidationMessage = (file: File) => {
    if (!isSupplierStatement) return null;

    if (!acceptedFormats.includes(file.type)) {
      return "This file format is not supported. Please upload PDF, JPG, or PNG files only.";
    }

    if (file.type === "application/pdf" && file.size > MAX_PDF_SIZE) {
      return `File ${file.name} exceeds the 40MB limit and cannot be uploaded.`;
    }

    if (file.type !== "application/pdf" && file.size > MAX_IMAGE_SIZE) {
      return `File ${file.name} exceeds the 10MB limit and cannot be uploaded.`;
    }

    return null;
  };

  const handleFilesInput = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isAllowed = await CheckUploadPermission();

    if (!isAllowed) {
      event.target.value = "";
      return;
    }

    const files = event.target.files;

    if (files) {
      const {
        validFiles,
        invalidFormatNames,
        oversizedFiles,
        overPageLimitFiles,
      } = await validateFiles(Array.from(files));

      if (invalidFormatNames.length > 0) {
        AlertMessage({
          text:
            invalidFormatNames.length === 1
              ? getSupplierStatementValidationMessage(
                  files[0] ?? new File([], invalidFormatNames[0])
                ) ||
                `This file format is not supported. Please upload ${isSupplierStatement ? "PDF, JPG, or PNG files only." : "a valid file type."}`
              : "This file format is not supported. Please upload PDF, JPG, or PNG files only.",
          type: "error",
          duration: 4,
        });
      }

      showOversizedFilesAlert(oversizedFiles);
      showOverPageLimitAlert(overPageLimitFiles);

      if (uploadedImages.length + validFiles.length > maxFileCount) {
        message.warning(
          `You can only upload a maximum of ${maxFileCount} files.`
        );
        event.target.value = "";
        return;
      }

      if (validFiles.length === 0) {
        event.target.value = "";
        return;
      }

      setUploadedImages((prevFiles) => [...prevFiles, ...validFiles]);
      event.target.value = "";
    }
  };

  const splitFilesIntoChunks = (files: File[]): File[][] => {
    const totalFiles = files.length;
    if (totalFiles === 0) return [];

    const baseChunkSize = Math.floor(totalFiles / 4);
    const extraFiles = totalFiles % 4;

    const chunks: File[][] = [];
    let startIndex = 0;

    for (let i = 0; i < 4 && startIndex < totalFiles; i++) {
      const chunkSize = baseChunkSize + (i < extraFiles ? 1 : 0);
      chunks.push(files.slice(startIndex, startIndex + chunkSize));
      startIndex += chunkSize;
    }

    return chunks;
  };

  const validateFiles = async (
    incoming: File[]
  ): Promise<{
    validFiles: File[];
    invalidFormatNames: string[];
    oversizedFiles: File[];
    overPageLimitFiles: File[];
  }> => {
    const invalidFormatNames: string[] = [];
    const oversizedFiles: File[] = [];
    const overPageLimitFiles: File[] = [];
    const validFiles: File[] = [];

    for (const file of incoming) {
      if (!acceptedFormats.includes(file.type)) {
        invalidFormatNames.push(file.name);
        continue;
      }

      const sizeLimit = getSizeLimitForFile(file);
      if (file.size > sizeLimit) {
        oversizedFiles.push(file);
        continue;
      }

      if (uploadMode === "per-page" && file.type === "application/pdf") {
        const pageCount = await getPdfPageCount(file);
        if (pageCount !== null && pageCount > MAX_PAGES_PER_PAGE_MODE) {
          overPageLimitFiles.push(file);
          continue;
        }
      }

      validFiles.push(file);
    }

    return { validFiles, invalidFormatNames, oversizedFiles, overPageLimitFiles };
  };

  const showOverPageLimitAlert = (overPageLimitFiles: File[]) => {
    if (overPageLimitFiles.length === 0) return;

    if (overPageLimitFiles.length === 1) {
      AlertMessage({
        text: `"${overPageLimitFiles[0].name}" exceeds the maximum of ${MAX_PAGES_PER_PAGE_MODE} pages allowed for One document per Page mode.`,
        type: "error",
        duration: 6,
      });
    } else {
      const names = overPageLimitFiles.map((f) => f.name).join(", ");
      AlertMessage({
        text: `The following files exceed the maximum of ${MAX_PAGES_PER_PAGE_MODE} pages allowed for One document per Page mode: ${names}`,
        type: "error",
        duration: 6,
      });
    }
  };

  const handleFileUpload = async (e: any) => {
    e.preventDefault();

    if (uploadedImages.length === 0) {
      AlertMessage({
        text: "Please select at least one file to upload.",
        type: "error",
        duration: 4,
      });
      return;
    }

    if (isUploading) {
      return;
    }

    setIsUploading(true);
    setcloseButon(true);
    document.body.style.pointerEvents = "none";

    const fileChunks = splitFilesIntoChunks(uploadedImages);
    let overallProgress = 0;

    try {
      setDisableInvoiceButton(true);
      const uploadResults: string[] = [];

      for (const chunk of fileChunks) {
        if (chunk.length === 0) continue;

        const chunkResults = await Promise.all(
          chunk.map((file) => AwsS3ImageService(selectedClient.userId, file))
        );

        uploadResults.push(chunkResults.join("$$$"));
        overallProgress += (1 / fileChunks.length) * 100;
        setUploadProgress(overallProgress);
      }

      const filePath = uploadResults.filter(Boolean).join("$$$");

      if (!filePath) {
        message.error("Failed to upload files. Please try again.");
      } else {
        await saveInvoicesToBackend(filePath);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      AlertMessage({
        text: "Something went wrong. The file could not be uploaded. Please try again.",
        type: "error",
        duration: 4,
      });
    } finally {
      document.body.style.pointerEvents = "auto";
      setDisableInvoiceButton(false);
      setUploadedImages([]);
      setIsUploading(false);
      setcloseButon(false);
      if (!isSupplierStatement) {
        onClose();
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files = event.dataTransfer.files;

    if (files) {
      const {
        validFiles,
        invalidFormatNames,
        oversizedFiles,
        overPageLimitFiles,
      } = await validateFiles(Array.from(files));

      if (invalidFormatNames.length > 0) {
        AlertMessage({
          text:
            invalidFormatNames.length === 1
              ? "This file format is not supported. Please upload PDF, JPG, or PNG files only."
              : "This file format is not supported. Please upload PDF, JPG, or PNG files only.",
          type: "error",
          duration: 10,
        });
      }

      showOversizedFilesAlert(oversizedFiles);
      showOverPageLimitAlert(overPageLimitFiles);

      if (uploadedImages.length + validFiles.length > maxFileCount) {
        message.warning(
          `You can only upload a maximum of ${maxFileCount} files.`
        );
        return;
      }

      if (validFiles.length === 0) {
        return;
      }

      setUploadedImages((prevFiles) => [...prevFiles, ...validFiles]);
    }
  };

  const saveInvoicesToBackend = async (filePath: string) => {
    try {
      dispatch(buttonFlag(true));
      localStorage.setItem("buttonStatus", true.toString());

      PostInvoiceImageService({
        filePath: filePath,
        clientUserId: SelectedClientID,
        uploadMode: uploadMode,
        onSuccess: onSuccess,
        dispatch,
      });

      const totalFileCount = filePath.split("$$$").length;

      if (totalFileCount >= 30) {
        AlertMessage({
          text: `We have received ${totalFileCount} files. Since the file count is large, processing may take approximately 15 minutes. Please wait while we handle your request.`,
          type: "info",
          duration: 10,
        });
      }

      setTimeout(() => { }, 5000);
      onClose();
    } catch (error) {
      console.error("Failed to save files to the backend", error);
    } finally {
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setUploadedImages((prevFiles) =>
      prevFiles.filter((file) => file !== fileToRemove)
    );

    AlertMessage({
      text: "File deleted successfully",
      type: "success",
      duration: 4,
    });
  };

  const OnCancel = () => {
    setUploadedImages([]);
    uploadCancelled.current = true;
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
    onClose();
  };

  const handleModeChange = (mode: UploadMode) => {
    if (isUploading || mode === uploadMode) return;

    const proceedWithChange = () => {
      setUploadMode(mode);
      setUploadedImages([]);
      const fileInput = document.getElementById(
        "file-upload"
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    };

    if (uploadedImages.length > 0) {
      Modal.confirm({
        title: "Change upload mode?",
        content:
          "Changing the upload mode will clear your current file selection. Do you want to continue?",
        okText: "Continue",
        cancelText: "Cancel",
        onOk: proceedWithChange,
        // onCancel intentionally left empty — keeps existing files and mode
      });
    } else {
      setUploadMode(mode);
    }
  };

  const modeCardStyle = (mode: UploadMode): React.CSSProperties => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "16px 12px",
    border:
      uploadMode === mode
        ? "1.5px solid #b0a8a8"
        : "1px solid #e0e0e0",
    borderRadius: "12px",
    backgroundColor: uploadMode === mode ? "#fdf4f4" : "#ffffff",
    cursor: isUploading ? "not-allowed" : "pointer",
    transition: "all 0.15s ease",
  });

  const radioStyle = (mode: UploadMode): React.CSSProperties => ({
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    border: uploadMode === mode ? "5px solid #333" : "1.5px solid #aaa",
    boxSizing: "border-box",
    flexShrink: 0,
  });

  return (
    <Modal
      open={visible}
      title={isSupplierStatement ? "Add documents" : "Add documents"}
      className={isSupplierStatement ? "supplier-statement-modal" : undefined}
      onCancel={OnCancel}
      closeIcon={<CancelBtn closeButon={closeButon} />}
      styles={{ mask: { backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" } }}
      footer={[
        <Button
          key="upload"
          type="primary"
          onClick={handleFileUpload}
          disabled={
            isUploading ||
            uploadedImages.length === 0 ||
            uploadedImages.length > maxFileCount
          }
          style={{
            width: "100%",
            height: "52px",
            margin: "0px",
            backgroundColor:
              isUploading ||
                uploadedImages.length === 0 ||
                uploadedImages.length > maxFileCount
                ? "#737373"
                : "var(--color-brand)",
            color: "white",
            borderRadius: "12px",
          }}
        >
          Upload
        </Button>,
      ]}
    >
      <div className="supplier-statement-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!showSupplierStatementView}
          className={!showSupplierStatementView ? "active" : undefined}
          onClick={() => {
            setShowSupplierStatementView(false);
            setUploadedImages([]);
            setUploadMode("per-file");
          }}
        >
          Costs
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={showSupplierStatementView}
          className={showSupplierStatementView ? "active" : undefined}
          onClick={() => {
            setShowSupplierStatementView(true);
            setUploadedImages([]);
            setUploadMode("per-file");
          }}
        >
          Supplier statements
        </button>
      </div>
      {showSupplierStatementView && (
        <>
          <p className="supplier-statement-description">
            Supplier Statements can be uploaded for processing and reconciliation.
            Supported file formats are PDF, JPG, and PNG. Upload one statement per
            file for the best results.
          </p>
          <p className="supplier-statement-upload-label">
            Upload from computer
          </p>
        </>
      )}

      {/* Upload Mode Selector */}
      {!isSupplierStatement && <div style={{ marginBottom: "20px" }}>
        <p
          style={{
            fontSize: "13px",
            color: "#888",
            marginBottom: "10px",
            fontWeight: 500,
          }}
        >
          Upload Mode
        </p>
        <div style={{ display: "flex", gap: "12px" }}>
          {/* One document per file */}
          <div
            style={modeCardStyle("per-file")}
            onClick={() => handleModeChange("per-file")}
          >
            <div style={radioStyle("per-file")} />
            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#333",
                textAlign: "center",
              }}
            >
              One document per file
            </span>
            <span style={{ fontSize: "11px", color: "#aaa" }}>
              PDF, JPG, PNG
            </span>
          </div>

          {/* One document per Page */}
          <div
            style={modeCardStyle("per-page")}
            onClick={() => handleModeChange("per-page")}
          >
            <div style={radioStyle("per-page")} />
            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#333",
                textAlign: "center",
              }}
            >
              One document per Page
            </span>
            <span style={{ fontSize: "11px", color: "#aaa" }}>
              PDF files only
            </span>
          </div>
        </div>
      </div>}

      {/* Drag and Drop Area */}
      {!isUploading && (
        <div
          className={`drag-drop-area ${dragging ? "dragging" : ""} ${isUploading ? "disabled" : ""
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            height: "120px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
          onClick={async () => {
            const isAllowed = await CheckUploadPermission();
            if (!isAllowed) {
              AlertMessage({
                text: "AI Service is Down.",
                subText:
                  "Some Features may be unavailable. We'll retry automatically.",
                type: "error",
                duration: 4,
              });
              return;
            }
            document.getElementById("file-upload")?.click();
          }}
        >
          {/* Cloud upload icon */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#aaa"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 16v-8m0 0-3 3m3-3 3 3" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
          <p style={{ paddingTop: "4px", fontWeight: "bold", margin: 0 }}>
            Drag and drop files here or click to upload
          </p>
          <span style={{ fontSize: "var(--font-size-sm)", color: "#666", fontWeight: 500 }}>
            {fileLimitHint}
          </span>
        </div>
      )}

      <input
        type="file"
        id="file-upload"
        style={{ display: "none" }}
        multiple
        accept={acceptAttr}
        onChange={handleFilesInput}
      />

      {uploadedImages.length > 0 && (
        <div style={{ textAlign: "center", marginTop: "12px" }}>
          <h5>
            {isSupplierStatement ? "Selected Supplier Statements" : "Total Number of Invoices"}: {uploadedImages.length} / {maxFileCount}
          </h5>
        </div>
      )}

      {uploadedImages.length > 0 && (
        <div className="image-preview">
          {uploadedImages.map((file, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#E3E4F6",
                borderRadius: "2px",
              }}
            >
              <p
                style={{
                  marginBottom: "0px",
                  marginTop: "0px",
                  marginRight: "10px",
                  maxWidth: "334px",
                  padding: "20px",
                }}
              >
                {file.name}
              </p>
              {!isUploading && (
                <DeleteOutlined
                  onClick={() => handleRemoveFile(file)}
                  style={{ color: "red", cursor: "pointer" }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {isUploading && (
        <div onClick={(e) => e.stopPropagation()}>
          <ProgressBar progress={uploadProgress} />
        </div>
      )}
    </Modal>
  );
};

export default InvoiceUploadModal;


